/**
 * galleries — studio-side gallery CRUD + the public gallery payload.
 *
 * DEMO-4: every mutation in here used to be Supabase-only, which is
 * why GalleryEditor hard-disabled Publish / Copy link / Upload /
 * Delete under Preview Studio — firing them would either 404 against a
 * demo id or, worse, mutate the studio's REAL gallery (the hub is
 * additive under Preview Studio, so both kinds of row are on screen at
 * once). Each mutation now has a demo branch that routes demo-scoped
 * ids into DemoStoreContext and REFUSES real ids with
 * `RealDataInPreviewError` — see src/lib/demoScope.ts for the rule.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { assertDemoScoped, isDemoId } from '@/lib/demoScope';
import { useAuth } from '@/hooks/useAuth';
// GAL-4 — gallery videos ride the existing Mux delivery pipeline.
// Each gallery gets ONE auto-created "companion" deliveries row
// (found by the sentinel description) that its video delivery_files
// hang off. The slug generator + sentinel live in deliveryService
// so both surfaces stay in lockstep.
import { generateSlug, GALLERY_COMPANION_DESCRIPTION } from '@/services/deliveryService';
// FIX-2 — gallery video tiles used to spin forever if the Mux webhook
// never fired. `sync-mux-status` is the rescue path; `isTranscoding`
// is the one shared definition of "Mux is still working".
import { syncMuxStatus } from '@/services/muxRecoveryService';
// GAL-14 — the interlock between this file and the migration that lets
// project_id go null. See src/config/launch.ts.
import { STANDALONE_GALLERIES } from '@/config/launch';
import { isTranscoding } from '@/lib/uploads/muxProcessingWatcher';
// GAL-10 — gallery designs + curated customization ride the row and
// the public payload. The PIN never does: `access_pin` is write-only
// (hashed by trigger, see migration 20260904120000) and only
// `pin_required` / `locked` come back out.
import type {
  GalleryCustomization,
  GalleryTemplateId,
} from '@/components/gallery/templates/types';
// GALLERY-2 — 00 Basic is the default design (the column default too).
import { DEFAULT_TEMPLATE_ID } from '@/components/gallery/templates/themes';

export interface Gallery {
  id: string;
  /// GAL-14 — null for a standalone gallery (no project, no client).
  /// Ownership no longer travels through the project: the row carries
  /// its own `org_id`, which is what every policy reads now.
  project_id: string | null;
  /// GAL-14 — the gallery's own org. Optional in the type only because
  /// the column arrives with the GAL-14 migration; it is NOT NULL in
  /// the database once that file is applied.
  org_id?: string;
  name: string;
  description: string | null;
  is_public: boolean;
  download_enabled: boolean;
  created_at: string;
  updated_at: string;
  /// Proofing — added by 20260501_gallery_proofing.sql. Default false
  /// so existing galleries are unaffected. When true, the portal
  /// shows favorite hearts + comment threads, and the studio sees
  /// picks aggregated.
  proofing_enabled?: boolean;
  /// Optional cap on total picks per client. null = unlimited.
  proofing_pick_limit?: number | null;
  /// Studio-authored message shown above the gallery in the portal.
  proofing_message?: string | null;
  /// Once true, no more picks/comments are accepted. Studio toggles
  /// this when the proofing window closes.
  proofing_locked?: boolean;
  /// Studio-designated hero photo for the client portal. Falls back
  /// to the first photo (sort_order = 0) when null. See migration
  /// 20260523_gallery_cover_photo.sql.
  cover_photo_id?: string | null;
  /// GAL-10 — which of the 23 designs renders this gallery. GAL-13
  /// retired 'classic'; Editorial is the DB default.
  template?: GalleryTemplateId;
  /// GAL-10 — curated visual preferences (accent, fonts, slideshow).
  customization?: GalleryCustomization;
  /// GAL-10 — WRITE-ONLY. Set a 4-digit code to gate the gallery, ''
  /// to remove it. The trigger hashes it before it hits disk; reads
  /// always come back null.
  access_pin?: string | null;
  /// GAL-10 — bcrypt of the code; `!!access_pin_hash` = PIN is set.
  access_pin_hash?: string | null;
}

export interface GalleryPhoto {
  id: string;
  gallery_id: string;
  file_url: string;
  thumbnail_url: string | null;
  name: string;
  sort_order: number;
  created_at: string;
  /// Wave 5 Feature 1 — Nova classification. Optional so old code
  /// paths compile cleanly; nova-tag-photo backfills after upload.
  ai_tags?: string[] | null;
  ai_scene?: string | null;
  ai_dominant_colors?: string[] | null;
  ai_tagged_at?: string | null;
  /// GAL-7 — true oriented pixel size captured at upload time (see
  /// migration 20260804090000). Null for pre-GAL-7 uploads; grids
  /// fall back to measure-on-load.
  width?: number | null;
  height?: number | null;
}

export interface GalleryInsert {
  /// GAL-14 — null creates a gallery that belongs to no project. Only
  /// legal once the GAL-14 migration has dropped the NOT NULL; the
  /// STANDALONE_GALLERIES flag is the interlock that decides whether a
  /// caller is allowed to send null yet.
  project_id: string | null;
  /// GAL-14 — the owning org, required by the post-migration schema
  /// (NOT NULL, no default). Callers send this only when
  /// STANDALONE_GALLERIES is on, because before the migration the
  /// column does not exist and PostgREST rejects the whole insert.
  org_id?: string;
  name?: string;
  description?: string;
  is_public?: boolean;
  download_enabled?: boolean;
  /// GAL-10 — new galleries are created WITH a design (the creation
  /// flow makes choosing one mandatory); Editorial is the DB default
  /// for anything that skips it.
  template?: GalleryTemplateId;
  customization?: GalleryCustomization;
  /// GAL-10 — write-only 4-digit code; hashed by the DB trigger.
  access_pin?: string;
}

export interface GalleryPhotoInsert {
  gallery_id: string;
  file_url: string;
  thumbnail_url?: string;
  name: string;
  sort_order?: number;
  /// GAL-7 — optional; stripped-and-retried when the dimensions
  /// migration hasn't been applied yet (see useAddGalleryPhoto).
  width?: number | null;
  height?: number | null;
}

/**
 * Org-wide gallery listing for the studio's /galleries hub.
 *
 * Returns every gallery across every project the studio owns, with
 * a cover thumbnail URL + photo count joined in so the masonry-style
 * list page can render rich cards without N+1 fetches.
 *
 * Sort order: newest gallery first (created_at desc). Galleries
 * without photos still appear so the studio sees "empty" galleries
 * they haven't uploaded to yet — selecting one drops them into the
 * upload flow where photos can be added.
 *
 * Demo mode returns [] for now; the demo seeder can backfill if we
 * want to populate the gallery hub in screenshot builds.
 */
export interface StudioGallery extends Gallery {
  /// Project name for the secondary line on each card ("Brennan Wedding").
  /// GAL-14 — null when the gallery belongs to no project.
  project_name: string | null;
  /// Client name for the tertiary line ("Aspen Lodge, May 2026").
  client_name: string | null;
  /// Public URL of the cover photo's thumbnail. Falls back to the
  /// first photo by sort_order when cover_photo_id is null. May be
  /// null if the gallery has no photos yet — UI should render a
  /// placeholder card in that case.
  cover_url: string | null;
  /// Total photos uploaded to this gallery. Lets the card show
  /// "47 photos" without a second query.
  photo_count: number;
}

/**
 * GAL-8 — the real-galleries fetch, shared by the normal path AND
 * the Preview Studio path below. Pulled out of the hook so demo
 * mode can MERGE real work in instead of replacing it.
 */
// Only this studio's galleries. RLS also lets anyone read *public*
// galleries (for the client view), so list queries must scope to the
// signed-in studio's org or other businesses' public galleries appear —
// and uploads into them are refused by gallery_photos RLS.
const currentOrgId = async (): Promise<string | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase.from('profiles').select('org_id').eq('id', user.id).maybeSingle();
  return (data?.org_id as string | null) ?? null;
};

const fetchStudioGalleries = async (): Promise<StudioGallery[]> => {
  // The studio's projects → each project's galleries → each
  // gallery's photos. PostgREST resource embedding lets us pull
  // it all in one round-trip; we resolve cover photo + count
  // client-side. `deleted_at` rides along so soft-deleted photos
  // (migration 20260202020943) don't inflate counts or become
  // covers — the public RPCs already exclude them.
  const { data, error } = await supabase
    .from('galleries')
    .select(`
      *,
      projects:project_id (
        name,
        client:clients ( name )
      ),
      gallery_photos!gallery_id ( id, file_url, thumbnail_url, sort_order, deleted_at )
    `)
    .eq('org_id', (await currentOrgId()) ?? '00000000-0000-0000-0000-000000000000')
    .order('created_at', { ascending: false });
  // GAL-9 — `!gallery_id` disambiguates the embed. The live DB now has
  // TWO relationships between galleries and gallery_photos (photos'
  // gallery_id FK, plus galleries.cover_photo_id pointing back at a
  // photo), and PostgREST refuses an ambiguous embed: "more than one
  // relationship was found" — which blanked the entire hub. We want
  // the photos-in-this-gallery direction, i.e. the gallery_id FK.

  if (error) throw error;

  return ((data ?? []) as any[]).map((row): StudioGallery => {
    const photos = ((row.gallery_photos ?? []) as Array<{
      id: string;
      file_url: string;
      thumbnail_url: string | null;
      sort_order: number;
      deleted_at?: string | null;
    }>).filter((p) => p.deleted_at == null);
    // Prefer the explicit cover; otherwise the first photo by
    // sort_order. Thumbnails preferred so the grid stays light;
    // fall through to the full file when no thumb exists (older
    // uploads that landed before the thumbnail pipeline).
    const cover =
      (row.cover_photo_id && photos.find((p) => p.id === row.cover_photo_id)) ||
      photos.sort((a, b) => a.sort_order - b.sort_order)[0] ||
      null;
    return {
      ...(row as Gallery),
      // GAL-14 — was 'Untitled project'. A standalone gallery has no
      // project at all, and labelling it "Untitled project" invents one
      // the studio never made. null, and the cards say "No project".
      project_name: row.projects?.name ?? null,
      client_name: row.projects?.client?.name ?? null,
      cover_url: cover ? (cover.thumbnail_url ?? cover.file_url) : null,
      photo_count: photos.length,
    };
  });
};

/**
 * DEMO-2 — result of `useStudioGalleries`, widened with one extra
 * field so the hub can tell "loaded, empty" apart from "failed".
 *
 * The NON-demo path already distinguishes them: `fetchStudioGalleries`
 * throws, react-query flips `isError`, and Galleries.tsx renders the
 * "Couldn't load your galleries." card (reading `.message` off the
 * raw object, because a Supabase PostgrestError is a PLAIN OBJECT and
 * `instanceof Error` is false for it — that check silently ate the
 * only useful clue once already).
 *
 * The Preview Studio path could NOT, because the real fetch was
 * wrapped in `.catch(() => [])` — the studio's real galleries just
 * quietly weren't there, indistinguishable from having none. Preview
 * Studio still must not crash on a network blip, so instead of
 * rethrowing we hand the failure out on `previewRealError`.
 */
export type PreviewRealFetchError = { message?: string } | null;

export const useStudioGalleries = () => {
  const { isDemo } = useDemoMode();
  const { user } = useAuth();
  const demoStore = useDemoStore();
  // DEMO-2 — set from inside queryFn (an async callback, not render),
  // so this is a legal setState. Cleared on every successful real
  // fetch so a retry that works clears the warning strip.
  const [previewRealError, setPreviewRealError] =
    useState<PreviewRealFetchError>(null);

  const query = useQuery({
    // DEMO-4 — store version in the key so a gallery created during
    // the demo appears on the hub without a refresh.
    queryKey: ['studio-galleries', user?.id, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async (): Promise<StudioGallery[]> => {
      if (isDemo) {
        // Demo galleries seeded in src/data/demoOverlay.ts (#317).
        // Imported lazily so demoOverlay isn't pulled into the
        // bundle for non-demo signed-in studios.
        //
        // GAL-8 — Preview Studio is ADDITIVE now, not a mask. The
        // old branch returned ONLY the demo cast, so any real
        // gallery vanished from the hub the moment Preview Studio
        // was on — which read exactly like "my galleries aren't
        // saving". Demo cards stay first (keynote recordings lead
        // with the curated cast); the studio's real work follows.
        // If the real fetch fails we still show the demo cast —
        // Preview Studio must never crash on a network blip.
        // DEMO-4 — read the STORE, not the frozen module, so galleries
        // created / published / renamed during the demo are on the hub
        // with the right chip. Falls back to the seed outside a
        // provider (tests).
        const stored = demoStore.galleries.list();
        const { demoGalleries } = await import('@/data/demoOverlay');
        const demo = (stored.length > 0
          ? stored
          : demoGalleries) as unknown as StudioGallery[];
        if (!user) return demo;
        // DEMO-2 — was `.catch(() => [])` with only a console.warn,
        // which made a failed load look exactly like "this studio has
        // no galleries". Still non-fatal, but the failure is now
        // reported out via `previewRealError` so the hub can say so.
        let real: StudioGallery[] = [];
        try {
          real = await fetchStudioGalleries();
          setPreviewRealError(null);
        } catch (err) {
          console.warn('[studio-galleries] real fetch failed under Preview Studio', err);
          // PostgrestError is a plain object, not an Error — read
          // `.message` off whatever shape arrived rather than testing
          // `instanceof Error` (which is false and drops the message).
          const message = (err as { message?: string } | null | undefined)?.message;
          setPreviewRealError({ message });
        }
        const demoIds = new Set(demo.map((g) => g.id));
        return [...demo, ...real.filter((g) => !demoIds.has(g.id))];
      }
      if (!user) return [];
      return fetchStudioGalleries();
    },
    enabled: !!user || isDemo,
  });

  // Spread rather than mutate so the returned object stays a plain
  // value. Only Galleries.tsx consumes this hook.
  return { ...query, previewRealError };
};

/**
 * GAL-5 — Single-gallery detail for the full-screen editor
 * (`/galleries/:galleryId`). Joins the owning project (name +
 * scheduled shoot date + client) so the editor's top bar can show
 * "Sarah + Ben · May 12, 2026" without a second query.
 *
 * Additive — nothing existing consumes this key. Editor-side
 * mutations that flip gallery fields should ALSO invalidate
 * `['studio-gallery', id]` (the editor page does this itself via
 * queryClient so the existing hooks stay untouched).
 */
export interface StudioGalleryDetail extends Gallery {
  /// GAL-14 — null for a standalone gallery.
  project_name: string | null;
  client_name: string | null;
  /// projects.scheduled_date — the shoot date shown in the editor
  /// top bar. Null when the project has no date.
  project_date: string | null;
  /// Wave 6 watermark opt-in — column exists post-migration but
  /// types.ts hasn't been regenerated, so it rides as optional.
  watermark_enabled?: boolean;
}

export const useStudioGallery = (galleryId: string | undefined) => {
  const { user } = useAuth();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useQuery({
    // DEMO-4 — keyed on the store version so a demo publish / rename /
    // cover change re-reads. Without it the editor's top bar kept
    // saying "Draft" after Publish had already flipped the row.
    queryKey: ['studio-gallery', galleryId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async (): Promise<StudioGalleryDetail | null> => {
      if (!galleryId) return null;
      if (isDemo) {
        // Store first — it holds both the seed and anything created or
        // edited in this session.
        const fromStore = demoStore.galleries
          .list()
          .find((g) => g.id === galleryId);
        if (fromStore) return fromStore as unknown as StudioGalleryDetail;
        const { demoGalleries } = await import('@/data/demoOverlay');
        const hit = (demoGalleries as unknown as StudioGalleryDetail[]).find(
          (g) => g.id === galleryId,
        );
        if (hit) return hit;
        // DEMO-4 — a demo-shaped id that resolved nowhere must NOT
        // fall through: `galleries.id` is a uuid column, so
        // `.eq('id', 'demo-g-abc')` errors with 22P02 and the editor
        // renders a failure instead of an honest "not here".
        if (isDemoId(galleryId)) return null;
        // GAL-8 — not a demo id → fall through to the real fetch.
        // The hub now lists real galleries alongside the demo cast,
        // so their editors must open even while Preview Studio is
        // on (writes stay blocked by the editor's own isDemo
        // guards). The old `return null` rendered "This gallery
        // isn't here." for real work — pure data-loss scare.
      }
      const { data, error } = await supabase
        .from('galleries')
        .select(`
          *,
          projects:project_id (
            name,
            scheduled_date,
            client:clients ( name )
          )
        `)
        .eq('id', galleryId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as any;
      return {
        ...(row as Gallery),
        // GAL-14 — null rather than an invented "Untitled project".
        project_name: row.projects?.name ?? null,
        client_name: row.projects?.client?.name ?? null,
        project_date: row.projects?.scheduled_date ?? null,
        watermark_enabled: row.watermark_enabled ?? false,
      };
    },
    enabled: (!!user || isDemo) && !!galleryId,
  });
};

export const useGalleries = (projectId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const { user } = useAuth();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['galleries', projectId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (!projectId) return [];

      // DEMO-4 — was `return []`, so ProjectWorkspace's "next action"
      // always said "no gallery yet" for demo projects even though
      // /galleries showed five of them, and creating one from the
      // project never appeared. Demo project ids resolve against the
      // store; a real project id under Preview Studio still hits
      // Supabase (read-only, so no leak).
      if (isDemo && isDemoId(projectId)) {
        return demoStore.galleries
          .list()
          .filter((g) => g.project_id === projectId) as unknown as Gallery[];
      }

      const { data, error } = await supabase
        .from('galleries')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Gallery[];
    },
    enabled: (!!user || isDemo) && !!projectId,
  });
};

export const useGalleryPhotos = (galleryId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const { user } = useAuth();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['gallery-photos', galleryId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (!galleryId) return [];

      if (isDemo) {
        // Seeded photo grids per gallery live in demoOverlay; the
        // store also supports session-scoped adds/updates so upload
        // + reorder actions in Preview Studio persist for the tab.
        // GALLERY-ONE — sorted, like the real query. The store keeps
        // rows in insertion order, so without this a demo reorder wrote
        // sort_order and the grid didn't move. It also brings Preview
        // Studio in line with production, where a new upload lands at
        // the END (its sort_order is the current photo count) rather
        // than at the front where `add` puts it.
        const rows = [...demoStore.galleryPhotos.list(galleryId)].sort(
          (a, b) => ((a.sort_order as number) ?? 0) - ((b.sort_order as number) ?? 0),
        );
        if (rows.length > 0) return rows as unknown as GalleryPhoto[];
        const { demoGalleryPhotos } = await import('@/data/demoOverlay');
        const seeded = demoGalleryPhotos[galleryId];
        if (seeded) return seeded as unknown as GalleryPhoto[];
        // DEMO-4 — a gallery CREATED during the demo has neither store
        // rows nor a seed entry, and its id isn't a uuid, so falling
        // through would send `demo-g-abc` at a uuid column and error
        // (22P02). An empty gallery is the correct answer, and it's
        // what makes the empty-state drop zone show up on stage.
        if (isDemoId(galleryId)) return [] as GalleryPhoto[];
        // GAL-8 — unknown id under Preview Studio = one of the
        // studio's REAL galleries (the hub lists them alongside the
        // cast now). Fall through to the real fetch so opening it
        // doesn't render an empty "your photos are gone" canvas.
      }

      const { data, error } = await supabase
        .from('gallery_photos')
        .select('*')
        .eq('gallery_id', galleryId)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as GalleryPhoto[];
    },
    enabled: (!!user || isDemo) && !!galleryId,
  });
};

// SEC-1 lockdown — the anon SELECT on projects-by-portal_token is
// gone (it exposed portal_token + budget + notes for every
// portal-enabled project everywhere), so the portal gallery hooks
// below go through the token-gated SECURITY DEFINER RPCs. Casts
// through `any` because types.ts hasn't been regenerated (house
// pattern, see deliveryService.ts).
export const useGalleriesByToken = (token: string | undefined) => {
  return useQuery({
    queryKey: ['galleries-by-token', token],
    queryFn: async () => {
      if (!token) return [];

      // RPC returns public galleries for the token's project,
      // newest first — same rows the old two-step read produced.
      const { data, error } = await (supabase as any).rpc(
        'get_public_portal_galleries',
        { p_token: token },
      );

      if (error) throw error;
      return ((data ?? []) as Gallery[]);
    },
    enabled: !!token,
  });
};

// SEC-1 — now token-scoped: the RPC verifies the gallery belongs to
// the token's project and is public, then returns its photos in
// sort order (soft-deleted rows excluded server-side).
export const useGalleryPhotosByToken = (
  token: string | undefined,
  galleryId: string | undefined,
) => {
  return useQuery({
    queryKey: ['gallery-photos-by-token', token, galleryId],
    queryFn: async () => {
      if (!token || !galleryId) return [];

      const { data, error } = await (supabase as any).rpc(
        'get_public_portal_gallery_photos',
        { p_token: token, p_gallery_id: galleryId },
      );

      if (error) throw error;
      return ((data ?? []) as GalleryPhoto[]);
    },
    enabled: !!token && !!galleryId,
  });
};

/**
 * Studio branding for the public client portal — pulled from the
 * organizations row that owns this token's project. Returns logo,
 * primary color, and studio name so the portal can render with
 * the studio's identity instead of generic Orbit chrome.
 *
 * Falls back to a sensible default brand color if the studio
 * never set one. logo_url stays null if missing — the UI shows
 * the studio name instead.
 */
export interface StudioBranding {
  studioName: string;
  logoUrl: string | null;
  brandColor: string;
}

export const useStudioBrandingByToken = (token: string | undefined) => {
  return useQuery({
    queryKey: ['studio-branding-by-token', token],
    queryFn: async (): Promise<StudioBranding | null> => {
      if (!token) return null;

      // SEC-1 lockdown — the projects-by-token and organizations
      // anon reads are both gone; one token-gated RPC hands back
      // just the branding trio. Same fallbacks as before.
      const { data: org, error } = await (supabase as any).rpc(
        'get_public_portal_branding',
        { p_token: token },
      );

      if (error || !org) return null;
      return {
        studioName: org.studio_name ?? 'Business',
        logoUrl: org.logo_url ?? null,
        brandColor: org.brand_color ?? '#C9A16B',
      };
    },
    enabled: !!token,
    staleTime: 5 * 60_000, // Brand info doesn't change often.
  });
};

/**
 * Cover photos for every gallery — used by the gallery picker
 * landing to show actual photo previews instead of empty cards.
 * Returns a map of gallery_id → cover photo's urls.
 *
 * Resolution rules (mirrored on the viewer side):
 *   1. If the gallery has `cover_photo_id` set, use that photo
 *      verbatim — the studio's deliberate hero choice.
 *   2. Otherwise fall back to the first photo by sort_order.
 *      Same as the original behavior before cover_photo_id
 *      was introduced; existing galleries get the same hero
 *      they always did.
 *
 * Two queries — one for the gallery rows (to read cover_photo_id)
 * and one for the photo rows. Both are bounded by the small list
 * of galleries the client is viewing, so the cost stays flat.
 */
export const useGalleryCoversByToken = (
  token: string | undefined,
  galleryIds: string[],
) => {
  return useQuery({
    queryKey: ['gallery-covers-by-token', token, galleryIds.sort().join(',')],
    queryFn: async (): Promise<Record<string, { fileUrl: string; thumbnailUrl: string | null }>> => {
      if (!token || galleryIds.length === 0) return {};

      // SEC-1 lockdown — anon can't read galleries/gallery_photos
      // by bare id anymore. Same two round-trips, both token-gated:
      // 1. Gallery rows (for cover_photo_id) come from
      //    get_public_portal_galleries.
      const { data: galleriesData } = await (supabase as any).rpc(
        'get_public_portal_galleries',
        { p_token: token },
      );
      const wanted = new Set(galleryIds);
      const galleries = ((galleriesData ?? []) as Gallery[])
        .filter((g) => wanted.has(g.id));

      // 2. Every photo we might use as a cover — p_gallery_id: null
      //    returns photos for ALL public galleries on the project,
      //    already sorted by sort_order so the fallback below stays
      //    "lowest sort_order per gallery".
      const { data: photosData, error } = await (supabase as any).rpc(
        'get_public_portal_gallery_photos',
        { p_token: token, p_gallery_id: null },
      );

      if (error || !photosData) return {};
      const photos = (photosData as GalleryPhoto[])
        .filter((p) => wanted.has(p.gallery_id));

      // Index explicit-cover photos by id for direct lookup.
      const photoById = new Map(photos.map((p) => [p.id, p]));

      const covers: Record<string, { fileUrl: string; thumbnailUrl: string | null }> = {};
      for (const g of galleries) {
        // 1. Explicit cover wins.
        if (g.cover_photo_id) {
          const p = photoById.get(g.cover_photo_id);
          if (p) {
            covers[g.id] = { fileUrl: p.file_url, thumbnailUrl: p.thumbnail_url };
            continue;
          }
          // (Cover was set but the photo's gone — drop through
          // to the first-photo fallback rather than show nothing.)
        }
        // 2. First-photo fallback.
        const firstPhoto = photos.find((p) => p.gallery_id === g.id);
        if (firstPhoto) {
          covers[g.id] = {
            fileUrl: firstPhoto.file_url,
            thumbnailUrl: firstPhoto.thumbnail_url,
          };
        }
      }
      return covers;
    },
    enabled: !!token && galleryIds.length > 0,
  });
};

/**
 * GAL-14 — one-time warning latch, same reason as the GAL-7 one below:
 * an unapplied migration is this project's most common failure mode and
 * must never be invisible, but it must also not print once per insert.
 */
let warnedMissingGalleryOrgColumn = false;

export const useCreateGallery = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async (gallery: GalleryInsert) => {
      // DEMO-4 — creating a gallery under Preview Studio used to insert
      // a REAL row (NewGalleryDialog had no demo guard at all), which
      // both broke the demo and left junk in the studio's own account.
      if (isDemo) {
        // GAL-14 — a standalone gallery has no project to scope-check.
        // The guard exists to stop Preview Studio writing onto a REAL
        // project; with no project there is nothing to write onto, and
        // the row never leaves the session store either way.
        if (gallery.project_id) {
          assertDemoScoped(gallery.project_id, 'Creating a gallery on a real project');
        }
        const nowIso = new Date().toISOString();
        const project = gallery.project_id
          ? demoStore.projects.list().find((p) => p.id === gallery.project_id)
          : undefined;
        const clientName =
          (project?.client as { name?: string } | undefined)?.name ??
          demoStore.clients.list().find((c) => c.id === project?.client_id)?.name ??
          null;
        return demoStore.galleries.add({
          id: `demo-g-${crypto.randomUUID().slice(0, 8)}`,
          project_id: gallery.project_id,
          name: gallery.name ?? 'Gallery',
          description: gallery.description ?? null,
          // GAL-10 — the demo public payload reads these back.
          template: gallery.template ?? DEFAULT_TEMPLATE_ID,
          customization: gallery.customization ?? {},
          // New galleries start as drafts, exactly like the live
          // default — the Publish beat has to have something to do.
          is_public: gallery.is_public ?? false,
          download_enabled: gallery.download_enabled ?? false,
          watermark_enabled: false,
          proofing_enabled: true,
          proofing_locked: false,
          proofing_pick_limit: null,
          proofing_message: null,
          cover_photo_id: null,
          project_name: (project?.name as string | undefined) ?? null,
          client_name: clientName,
          project_date:
            (project?.scheduled_date as string | null | undefined) ?? null,
          cover_url: null,
          photo_count: 0,
          created_at: nowIso,
          updated_at: nowIso,
        }) as unknown as Gallery;
      }

      // GAL-14 · GENERATED-TYPE DRIFT, deliberate and narrow.
      //
      // types.ts is generated from the LIVE schema, which still has
      // `project_id NOT NULL` and no `org_id` — the GAL-14 migration
      // adds both. Two options: hand-edit the generated file, or cast
      // here. Hand-editing loses the moment Lovable regenerates it,
      // and regenerating BEFORE the migration would put the old shape
      // back and break the build. The cast survives both directions,
      // and is scoped to this one argument so every other field on the
      // insert is still checked.
      const insertRow = async (payload: GalleryInsert) =>
        supabase.from('galleries').insert(payload as never).select().single();

      let { data, error } = await insertRow(gallery);

      // ── GAL-14 · the migration may not be applied on this database ──
      //
      // Same shape as the GAL-7 dimensions fallback below: the client
      // asks for the post-migration schema, and degrades to the old one
      // rather than failing, LOUDLY, once.
      const code = (error as { code?: string } | null)?.code;

      // PGRST204 — PostgREST doesn't know the column. Only org_id can
      // produce this here, and only when we sent it.
      if (error && code === 'PGRST204' && gallery.org_id) {
        if (!warnedMissingGalleryOrgColumn) {
          warnedMissingGalleryOrgColumn = true;
          console.warn(
            '[GAL-14] galleries.org_id does not exist on this database — ' +
              'supabase/migrations/20260915120000_gal14_standalone_galleries.sql has not been applied. ' +
              'Retrying without it. Galleries still resolve their org through the project until it is.',
          );
        }
        const { org_id: _unapplied, ...withoutOrg } = gallery;
        ({ data, error } = await insertRow(withoutOrg));
      }

      // 23502 — NOT NULL violation. With no project, that is project_id,
      // and it means the column is still NOT NULL. Nothing the client
      // can do about it, so say precisely what is wrong instead of
      // letting "Failed to create gallery" carry the blame.
      if (error && (error as { code?: string }).code === '23502' && !gallery.project_id) {
        throw new Error(
          'Galleries still need a project on this database. The change that makes it optional ' +
            '(20260915120000_gal14_standalone_galleries.sql) has not been applied yet — ' +
            'pick a project for now, or apply that migration.',
        );
      }

      if (error) throw error;
      return data as unknown as Gallery;
    },
    onSuccess: (data) => {
      if (data.project_id) {
        queryClient.invalidateQueries({ queryKey: ['galleries', data.project_id] });
      } else {
        // GAL-14 — a gallery made with no project belongs on the list a
        // project's "Attach existing" reads from, immediately.
        queryClient.invalidateQueries({ queryKey: ['unattached-galleries'] });
      }
      // GAL-7 — the /galleries hub reads ['studio-galleries', …] and
      // the app-wide staleTime is 5 minutes. Without this, a freshly
      // created gallery didn't show on the hub until the cache aged
      // out — "I created a gallery, left the page, and it was gone."
      // Partial-key match invalidates every user/demo variant.
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
    },
  });
};

export const useUpdateGallery = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, projectId, ...updates }: Partial<Gallery> & { id: string; projectId: string | null }) => {
      // DEMO-4 — this is the mutation Publish / Unpublish / rename /
      // downloads / watermark all go through. Demo ids mutate the
      // store; a REAL gallery id under Preview Studio is refused, so
      // Preview Studio can never flip real client work live.
      if (isDemo) {
        assertDemoScoped(id, 'Editing a real gallery');
        const merged = demoStore.galleries.update(id, updates as any);
        if (!merged) {
          throw new Error(
            "That gallery isn't in this Preview Studio session anymore — refresh to reset the example data.",
          );
        }
        return { ...merged, projectId } as unknown as Gallery & { projectId: string | null };
      }

      // GAL-14 — same generated-type drift as the insert above: these
      // updates can carry `org_id`, which types.ts does not know yet.
      const { data, error } = await supabase
        .from('galleries')
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, projectId } as unknown as Gallery & { projectId: string | null };
    },
    onSuccess: (data) => {
      if (data.projectId) {
        queryClient.invalidateQueries({ queryKey: ['galleries', data.projectId] });
      }
      // GAL-7 — hub cards show name/live-status; keep them honest.
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
      // DEMO-4 — the public gallery page reads its own key; a publish
      // has to reach it or "open the client gallery" shows the stale
      // draft state.
      queryClient.invalidateQueries({ queryKey: ['public-gallery', data.id] });
    },
  });
};

/**
 * GAL-14 — the galleries that belong to no project yet.
 *
 * The other half of "it should not be required to attach to a project,
 * however in the settings of the project you should have the option to
 * if you want to." Creating a gallery no longer needs a project; this
 * is how a project goes and fetches one afterwards.
 *
 * Scope: the studio's own galleries with `project_id IS NULL`. There is
 * no org filter in the query because there is no need for one — RLS
 * already limits `galleries` to the caller's org, and adding a client
 * side `.eq('org_id', ...)` would only invent a second, weaker copy of
 * that rule which could drift from it.
 *
 * Disabled entirely until the migration lands: before it, `project_id`
 * is NOT NULL, so this query is guaranteed to return zero rows and the
 * round-trip is pure waste.
 */
export const useUnattachedGalleries = () => {
  const { user } = useAuth();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['unattached-galleries', user?.id, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async (): Promise<Gallery[]> => {
      if (isDemo) {
        return demoStore.galleries
          .list()
          .filter((g) => !g.project_id) as unknown as Gallery[];
      }
      const { data, error } = await supabase
        .from('galleries')
        .select('*')
        .is('project_id', null)
        .eq('org_id', (await currentOrgId()) ?? '00000000-0000-0000-0000-000000000000')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as Gallery[];
    },
    enabled: STANDALONE_GALLERIES && (!!user || isDemo),
  });
};

/**
 * GAL-14 — attach a gallery to a project, or detach it from one.
 *
 * `projectId: null` detaches. Separate from `useUpdateGallery` for one
 * reason that matters: this mutation moves a row BETWEEN two project
 * caches, so it has to invalidate the one it left as well as the one it
 * joined. `useUpdateGallery` only ever knows one project id, so routing
 * a move through it would leave the old project's tab still showing a
 * gallery that is no longer in it — the kind of staleness that reads as
 * "the attach didn't work" and gets done twice.
 */
export const useSetGalleryProject = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      previousProjectId = null,
    }: {
      id: string;
      projectId: string | null;
      previousProjectId?: string | null;
    }) => {
      if (isDemo) {
        assertDemoScoped(id, 'Moving a real gallery between projects');
        const merged = demoStore.galleries.update(id, { project_id: projectId } as any);
        if (!merged) {
          throw new Error(
            "That gallery isn't in this Preview Studio session anymore — refresh to reset the example data.",
          );
        }
        return { id, projectId, previousProjectId };
      }
      // GAL-14 — `project_id: null` is the whole point of this mutation
      // and is exactly what the un-regenerated types.ts forbids.
      const { error } = await supabase
        .from('galleries')
        .update({ project_id: projectId } as never)
        .eq('id', id);
      if (error) throw error;
      return { id, projectId, previousProjectId };
    },
    onSuccess: ({ id, projectId, previousProjectId }) => {
      for (const pid of [projectId, previousProjectId]) {
        if (pid) queryClient.invalidateQueries({ queryKey: ['galleries', pid] });
      }
      queryClient.invalidateQueries({ queryKey: ['unattached-galleries'] });
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
      queryClient.invalidateQueries({ queryKey: ['studio-gallery', id] });
    },
  });
};

/**
 * Designate a photo as the gallery's hero / cover image. Setting
 * `photoId` to null clears the cover and falls back to the first
 * photo in sort order (matches the portal's default behavior).
 *
 * Mutation invalidates both the studio-side galleries cache AND
 * the portal-side caches so the cover swap shows up everywhere.
 */
export const useSetGalleryCover = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({
      galleryId,
      photoId,
      projectId,
    }: {
      galleryId: string;
      photoId: string | null;
      projectId?: string | null;
    }) => {
      // DEMO-4 — picking the hero frame is part of the gallery-build
      // beat; it was a toast that said no.
      if (isDemo) {
        assertDemoScoped(galleryId, 'Changing the cover on a real gallery');
        const merged = demoStore.galleries.update(galleryId, {
          cover_photo_id: photoId,
        });
        if (!merged) throw new Error("That gallery isn't in this session anymore.");
        return { galleryId, photoId, projectId };
      }

      const { error } = await supabase
        .from('galleries')
        .update({ cover_photo_id: photoId })
        .eq('id', galleryId);
      if (error) throw error;
      return { galleryId, photoId, projectId };
    },
    onSuccess: ({ projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      // GAL-7 — hub cards render the cover; ['galleries'] does not
      // partial-match ['studio-galleries'].
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['galleries', projectId] });
      }
      // Portal-side caches — the public gallery list and its
      // cover thumbnails. Token isn't known here so we invalidate
      // the broad keys; staleTime keeps the cost minimal.
      queryClient.invalidateQueries({ queryKey: ['galleries-by-token'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-covers-by-token'] });
    },
  });
};

export const useDeleteGallery = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string | null }) => {
      if (isDemo) {
        // DEMO-4 — deleting a REAL gallery from Preview Studio is the
        // single most destructive thing this mode could do. Refused.
        assertDemoScoped(id, 'Deleting a real gallery');
        demoStore.galleries.remove(id);
        return { id, projectId };
      }

      const { error } = await supabase
        .from('galleries')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, projectId };
    },
    onSuccess: (data) => {
      // GAL-14 — a standalone gallery has no per-project cache to clear.
      if (data.projectId) {
        queryClient.invalidateQueries({ queryKey: ['galleries', data.projectId] });
      }
      queryClient.invalidateQueries({ queryKey: ['unattached-galleries'] });
      // GAL-7 — same staleness family as useCreateGallery: a deleted
      // gallery kept showing on the hub for up to 5 minutes.
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
    },
  });
};

// GAL-7 — one-time warning latch. If the dimensions migration
// (20260804090000) hasn't been applied, we strip width/height and
// retry so uploads never break — but we say so LOUDLY, once, because
// unapplied migrations are this project's #1 recurring failure mode
// and must never be invisible.
let warnedMissingDimensionColumns = false;

export const useAddGalleryPhoto = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async (photo: GalleryPhotoInsert) => {
      // DEMO-4 — the "drop photos in" beat. Demo photos are object
      // URLs held by the browser for the tab's lifetime (see
      // GalleryEditor.handleUpload); nothing is uploaded to storage
      // and no row is inserted, so there is nothing to clean up.
      if (isDemo) {
        assertDemoScoped(photo.gallery_id, 'Uploading to a real gallery');
        const nowIso = new Date().toISOString();
        const added = demoStore.galleryPhotos.add(photo.gallery_id, {
          id: `demo-ph-${crypto.randomUUID().slice(0, 8)}`,
          gallery_id: photo.gallery_id,
          file_url: photo.file_url,
          url: photo.file_url,
          thumbnail_url: photo.thumbnail_url ?? photo.file_url,
          name: photo.name,
          sort_order: photo.sort_order ?? 0,
          width: photo.width ?? null,
          height: photo.height ?? null,
          ai_tags: null,
          ai_scene: null,
          ai_dominant_colors: null,
          ai_tagged_at: null,
          created_at: nowIso,
          updated_at: nowIso,
        });
        // Keep the hub card's "N photos" honest.
        const gallery = demoStore.galleries
          .list()
          .find((g) => g.id === photo.gallery_id);
        if (gallery) {
          demoStore.galleries.update(photo.gallery_id, {
            photo_count: demoStore.galleryPhotos.list(photo.gallery_id).length,
            cover_url:
              (gallery.cover_url as string | null) ??
              (photo.thumbnail_url ?? photo.file_url),
          });
        }
        return added as unknown as GalleryPhoto;
      }

      const { data, error } = await supabase
        .from('gallery_photos')
        .insert(photo)
        .select()
        .single();

      if (error) {
        // PGRST204 = PostgREST "column not found in schema cache";
        // 42703 = Postgres undefined_column. Either means the GAL-7
        // migration is missing from the live DB.
        const columnMissing =
          (photo.width != null || photo.height != null) &&
          (error.code === 'PGRST204' || error.code === '42703') &&
          /width|height/i.test(error.message ?? '');
        if (!columnMissing) throw error;

        if (!warnedMissingDimensionColumns) {
          warnedMissingDimensionColumns = true;
          toast.error('Gallery migration missing', {
            description:
              'Photos are saving without their dimensions — apply migration 20260804090000_gal7_gallery_photo_dimensions so grids stop reflowing.',
            duration: 12000,
          });
        }
        const { width: _w, height: _h, ...withoutDims } = photo;
        const retry = await supabase
          .from('gallery_photos')
          .insert(withoutDims)
          .select()
          .single();
        if (retry.error) throw retry.error;
        return retry.data as GalleryPhoto;
      }
      return data as GalleryPhoto;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['gallery-photos', data.gallery_id] });
      // GAL-7 — photo counts + cover fallbacks on the hub cards.
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
    },
  });
};

export const useDeleteGalleryPhoto = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, galleryId }: { id: string; galleryId: string }) => {
      if (isDemo) {
        assertDemoScoped(galleryId, 'Deleting photos from a real gallery');
        demoStore.galleryPhotos.remove(galleryId, id);
        demoStore.galleries.update(galleryId, {
          photo_count: demoStore.galleryPhotos.list(galleryId).length,
        });
        return { id, galleryId };
      }

      const { error } = await supabase
        .from('gallery_photos')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, galleryId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['gallery-photos', data.galleryId] });
      // GAL-7 — hub photo counts / cover fallback.
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
    },
  });
};

/**
 * GALLERY-ONE — the studio drags photos into the order the client sees.
 *
 * `sort_order` has always existed and both the editor and the public
 * gallery already read it; what was missing was a way to write it. One
 * UPDATE per row rather than an upsert, because an upsert on
 * `gallery_photos` would need every NOT NULL column echoed back and a
 * single wrong value would rewrite a photo's url.
 *
 * The caller holds the new order locally and rolls back if this throws,
 * so a failed save never leaves the studio looking at an order the
 * client won't get.
 */
export const useReorderGalleryPhotos = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({
      galleryId,
      photos,
    }: {
      galleryId: string;
      photos: { id: string; sort_order: number }[];
    }) => {
      if (isDemo) {
        assertDemoScoped(galleryId, 'Reordering a real gallery');
        for (const row of photos) {
          demoStore.galleryPhotos.update(galleryId, row.id, { sort_order: row.sort_order });
        }
        return { galleryId };
      }

      const results = await Promise.all(
        photos.map((row) =>
          supabase
            .from('gallery_photos')
            .update({ sort_order: row.sort_order })
            .eq('id', row.id)
            .eq('gallery_id', galleryId),
        ),
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
      return { galleryId };
    },
    onSuccess: ({ galleryId }) => {
      queryClient.invalidateQueries({ queryKey: ['gallery-photos', galleryId] });
      // The hub's cover falls back to the first photo by sort_order, so
      // a reorder can change the tile the studio sees on the hub.
      queryClient.invalidateQueries({ queryKey: ['studio-galleries'] });
    },
  });
};

/**
 * GALLERY-ONE — the same for films.
 *
 * `delivery_files.sort_order` existed and was never read: the films
 * query ordered by `created_at` and didn't even select the column. It
 * does both now, so this writes somewhere that shows.
 *
 * Scoped by `delivery_id` rather than gallery: films are
 * `delivery_files` rows on a hidden companion delivery, so the gallery
 * id isn't on the row to check against.
 */
export const useReorderGalleryVideos = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();

  return useMutation({
    mutationFn: async ({
      galleryId,
      videos,
    }: {
      galleryId: string;
      videos: { id: string; delivery_id: string; sort_order: number }[];
    }) => {
      // DEMO-4 — the demo cast has no gallery films (they'd need a live
      // Mux asset), so there is nothing to reorder under Preview Studio
      // and nothing to refuse either.
      if (isDemo && isDemoId(galleryId)) return { galleryId };

      const results = await Promise.all(
        videos.map((row) =>
          supabase
            .from('delivery_files')
            .update({ sort_order: row.sort_order })
            .eq('id', row.id)
            .eq('delivery_id', row.delivery_id),
        ),
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
      return { galleryId };
    },
    onSuccess: ({ galleryId }) => {
      queryClient.invalidateQueries({ queryKey: ['gallery-videos', galleryId] });
    },
  });
};

// ────────────────────────────────────────────────────────────────
// GAL-2 — Standalone public gallery portal (`/gallery/:galleryId`)
//
// Everything above is either studio-side (auth required) or scoped
// to a project portal token. The new public gallery page fetches
// through ONE SECURITY DEFINER RPC — `get_public_gallery_by_id` —
// which bundles gallery meta + photos + studio branding + watermark
// + any Mux videos from a linked ungated delivery. See the migration
// `20260728260000_gal2_public_gallery_rpc.sql` for the field list
// and the RLS-bypass rationale (org branding was locked to auth
// readers only after Phase 1, so the client portal couldn't fetch
// it without this RPC).
// ────────────────────────────────────────────────────────────────

export interface PublicGalleryPayload {
  /// GAL-10 — true when the gallery has a PIN and this payload was
  /// fetched without it: `photos` and `videos` are empty until
  /// `unlock_gallery_pin` succeeds (the engine swaps the payload).
  locked?: boolean;
  gallery: {
    id: string;
    name: string;
    description: string | null;
    is_public: boolean;
    download_enabled: boolean;
    proofing_enabled: boolean;
    proofing_locked: boolean;
    proofing_pick_limit: number | null;
    proofing_message: string | null;
    cover_photo_id: string | null;
    created_at: string;
    updated_at: string;
    /// GAL-10 — present once migration 20260904120000 is applied.
    template?: GalleryTemplateId;
    customization?: GalleryCustomization;
    pin_required?: boolean;
  };
  photos: Array<{
    id: string;
    file_url: string;
    thumbnail_url: string | null;
    name: string;
    sort_order: number;
    /// GAL-7 — present once migration 20260804090000 is applied
    /// (and only for photos uploaded after it). Grids treat missing
    /// dims as "measure on load".
    width?: number | null;
    height?: number | null;
    ai_tags?: string[] | null;
    ai_scene?: string | null;
    ai_dominant_colors?: string[] | null;
  }>;
  branding: {
    studio_name: string;
    logo_url: string | null;
    brand_color: string;
  } | null;
  watermark: {
    image_url: string | null;
    text: string | null;
    position: string;
    opacity: number;
    scale: number;
  } | null;
  videos: Array<{
    id: string;
    file_name: string;
    mux_playback_id: string;
    mux_status: string;
    duration_seconds: number | null;
    /// PLANS-3 — plan-correct mp4 URL (highest.mp4 on Business).
    /// Absent/null until migration 20260805000000 is applied and the
    /// webhook has seen the asset; fall back to capped-1080p.mp4.
    mux_mp4_url?: string | null;
    /// GAL-14 — poster override for sample films in the live design
    /// thumbnails (no Mux asset behind them). Never set by the RPC.
    poster_url?: string | null;
  }>;
}

// ────────────────────────────────────────────────────────────────
// GAL-4 — Gallery videos (Mux pipeline)
//
// Videos in a gallery are `delivery_files` rows (file_type='video')
// hanging off a companion `deliveries` row with gallery_id set.
// Upload path: ensureGalleryDelivery → create-mux-upload edge fn →
// PUT bytes to the returned Mux URL → mux-webhook / sync-mux-status
// flips mux_status to 'ready'. The public gallery RPC
// (get_public_gallery_by_id) already returns ready videos from any
// password-less, unexpired delivery linked to the gallery.
// ────────────────────────────────────────────────────────────────

/**
 * Find-or-create the companion delivery that holds a gallery's
 * videos. We ONLY reuse rows carrying the sentinel description so a
 * delivery the studio deliberately linked to this gallery (possibly
 * password-gated) is never hijacked as upload storage.
 *
 * Columns inserted (deliveries schema requires client_name / slug /
 * title / user_id):
 *   user_id     — the uploading studio user
 *   title       — "<gallery name> — videos"
 *   client_name — the gallery name (never surfaced; column is NOT NULL)
 *   slug        — 8-char slug via the shared generateSlug()
 *   gallery_id  — the gallery
 *   description — GALLERY_COMPANION_DESCRIPTION sentinel
 *   password    — null (public RPC only exposes password-less videos)
 *   expires_at  — null (never expires)
 *   project_id  — null ON PURPOSE: linking it to the project would
 *                 surface this plumbing row in the project portal's
 *                 deliveries tab via get_public_portal_deliveries
 *                 (SEC-1's token-gated replacement for the old
 *                 get_public_deliveries_by_project).
 */
export const ensureGalleryDelivery = async (
  galleryId: string,
  galleryName: string,
  userId: string,
): Promise<{ id: string }> => {
  const { data: existing, error: findError } = await supabase
    .from('deliveries')
    .select('id')
    .eq('gallery_id', galleryId)
    .eq('description', GALLERY_COMPANION_DESCRIPTION)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (findError) throw findError;
  if (existing) return existing;

  const { data, error } = await supabase
    .from('deliveries')
    .insert({
      user_id: userId,
      title: `${galleryName} — videos`,
      client_name: galleryName,
      slug: generateSlug(),
      gallery_id: galleryId,
      description: GALLERY_COMPANION_DESCRIPTION,
      password: null,
      expires_at: null,
      project_id: null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data as { id: string };
};

/** Studio-side shape for a gallery video tile. */
export interface GalleryVideo {
  id: string;
  delivery_id: string;
  file_name: string;
  file_size: number | null;
  /**
   * VIDEO-1 — needed by the original-master hand-off. `get-video-master`
   * refuses any row without a Mux asset id (400 "Not a Mux video"), so
   * the UI reads it to say "no original on file" instead of firing a
   * request it knows will fail.
   */
  mux_asset_id: string | null;
  mux_playback_id: string | null;
  mux_status: string | null;
  duration_seconds: number | null;
  created_at: string;
  /**
   * GALLERY-ONE — the studio's own order. The column existed from the
   * start and nothing read it; films came out in upload order. Null on
   * every film uploaded before this, which is why the query sorts nulls
   * last and falls back to `created_at`.
   */
  sort_order: number | null;
}

// ── FIX-2 — the gallery grid's rescue path ────────────────────────
//
// Before FIX-2 this hook polled `delivery_files` every 8 s while any
// video was transcoding — but NOTHING in that loop ever asked Mux for
// the truth. The row only changed if `mux-webhook` fired. With
// MUX_WEBHOOK_SECRET unset the webhook refuses every request (503, by
// design, SEC-1), so the row stayed 'preparing' forever and the tile
// spun forever, on every page load, for the rest of time.
//
// Now each poll first calls `sync-mux-status`, which reads the Mux API
// with the service role and writes the real status back. That makes
// the gallery grid survive a dead webhook. Throttled per delivery so
// a page with several galleries open doesn't stampede the function.
const lastGallerySyncAt = new Map<string, number>();
const GALLERY_SYNC_THROTTLE_MS = 10000;

async function rescueGalleryVideoStatuses(deliveryIds: string[]) {
  const now = Date.now();
  const due = deliveryIds.filter(
    (id) => now - (lastGallerySyncAt.get(id) ?? 0) >= GALLERY_SYNC_THROTTLE_MS,
  );
  if (due.length === 0) return;
  for (const id of due) lastGallerySyncAt.set(id, now);
  // allSettled + a swallowed catch: this is a best-effort rescue, and
  // a failing sync must never break the video list itself.
  await Promise.allSettled(
    due.map((deliveryId) =>
      syncMuxStatus({ deliveryId }).catch((err) => {
        console.warn('[FIX-2] gallery sync-mux-status failed', deliveryId, err);
        return null;
      }),
    ),
  );
}

/**
 * Studio-side list of a gallery's videos — every delivery_files row
 * with file_type='video' on ANY delivery linked to this gallery
 * (companion or manually linked), so the studio grid matches the
 * breadth of the public RPC. Auto-polls every 8s while any video is
 * still transcoding so "Processing…" tiles flip to posters without
 * a refresh — and (FIX-2) pulls the truth from Mux on each pass so
 * those tiles resolve even when the webhook never fires.
 */
export const useGalleryVideos = (galleryId: string | undefined) => {
  const { user } = useAuth();
  const { isDemo } = useDemoMode();

  return useQuery({
    queryKey: ['gallery-videos', galleryId],
    queryFn: async (): Promise<GalleryVideo[]> => {
      if (!galleryId) return [];
      // DEMO-4 — the demo cast has no gallery videos: they'd need a
      // companion delivery + a live Mux asset, and there's no way to
      // fake a playable stream. This used to short-circuit on `isDemo`
      // alone, which also blanked the video row on the studio's REAL
      // galleries while Preview Studio was on. Now only demo ids
      // return empty; real ids still list their films.
      if (isDemoId(galleryId)) return [];
      const { data: deliveries, error: dError } = await supabase
        .from('deliveries')
        .select('id')
        .eq('gallery_id', galleryId);
      if (dError) throw dError;
      const deliveryIds = (deliveries ?? []).map((d) => d.id);
      if (deliveryIds.length === 0) return [];

      const select =
        'id, delivery_id, file_name, file_size, mux_asset_id, mux_playback_id, mux_status, duration_seconds, created_at, sort_order';

      const { data, error } = await supabase
        .from('delivery_files')
        .select(select)
        .in('delivery_id', deliveryIds)
        .eq('file_type', 'video')
        // GALLERY-ONE — the studio's order first, upload order for the
        // films that predate it. nullsFirst: false keeps those behind
        // anything deliberately placed.
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as GalleryVideo[];

      // FIX-2 — if anything is mid-transcode, ask Mux directly and
      // re-read. Wrapped so a rescue failure can never fail the query
      // (the grid still renders with whatever the DB last said).
      //
      // DEMO-4 — but not while Preview Studio is on. `sync-mux-status`
      // WRITES the real status back to real `delivery_files` rows, and
      // Preview Studio may not mutate real studio data even helpfully.
      // The tiles still render; they just don't self-heal until the
      // studio leaves Preview Studio.
      const stillTranscoding = isDemo
        ? []
        : rows.filter((v) => isTranscoding(v.mux_status));
      if (stillTranscoding.length === 0) return rows;

      try {
        await rescueGalleryVideoStatuses([
          ...new Set(stillTranscoding.map((v) => v.delivery_id)),
        ]);
        const refreshed = await supabase
          .from('delivery_files')
          .select(select)
          .in('delivery_id', deliveryIds)
          .eq('file_type', 'video')
          .order('sort_order', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: true });
        if (!refreshed.error && refreshed.data) {
          return refreshed.data as GalleryVideo[];
        }
      } catch (err) {
        console.warn('[FIX-2] gallery video status rescue failed', err);
      }
      return rows;
    },
    enabled: (!!user || isDemo) && !!galleryId,
    refetchInterval: (query) => {
      const rows = query.state.data as GalleryVideo[] | undefined;
      const transcoding = rows?.some((v) => isTranscoding(v.mux_status));
      return transcoding ? 8000 : false;
    },
  });
};

/** Delete a gallery video (delivery_files row). The Mux asset itself
 *  is left to the existing delivery cleanup lifecycle. */
export const useDeleteGalleryVideo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, galleryId }: { id: string; galleryId: string }) => {
      const { error } = await supabase.from('delivery_files').delete().eq('id', id);
      if (error) throw error;
      return { id, galleryId };
    },
    onSuccess: ({ galleryId }) => {
      queryClient.invalidateQueries({ queryKey: ['gallery-videos', galleryId] });
      queryClient.invalidateQueries({ queryKey: ['public-gallery', galleryId] });
    },
  });
};

/**
 * DEMO-4 — resolve a demo gallery into the same public payload shape
 * the RPC returns, so `/gallery/demo-g-…` renders the real client
 * experience (hero, justified grid, hearts, lightbox) rather than
 * "Gallery not found."
 *
 * WHY THIS IS NOT A LEAK
 * ----------------------
 * The branch below only runs for a viewer who is BOTH signed in and
 * admin-granted Preview Studio access (`canUseDemo`, read from
 * `profiles.demo_enabled` — see DemoModeContext). To anyone else on
 * the internet, `/gallery/demo-g-1` falls straight through to the RPC,
 * which has no such row and returns null → "Gallery not found." So:
 *
 *   • demo content is never publicly reachable, and
 *   • the moment an admin revokes demo access the link stops
 *     resolving — it cannot outlive the demo.
 *
 * Nothing here touches Supabase or storage; the photos are the
 * MEDIA-2 bundle files (or, for photos added during the session,
 * in-memory object URLs that die with the tab).
 */
function buildDemoPublicPayload(
  gallery: Record<string, any>,
  photos: Record<string, any>[],
): PublicGalleryPayload {
  return {
    gallery: {
      id: gallery.id,
      name: gallery.name,
      description: gallery.description ?? null,
      is_public: !!gallery.is_public,
      download_enabled: !!gallery.download_enabled,
      proofing_enabled: !!gallery.proofing_enabled,
      proofing_locked: !!gallery.proofing_locked,
      proofing_pick_limit: gallery.proofing_pick_limit ?? null,
      proofing_message: gallery.proofing_message ?? null,
      cover_photo_id: gallery.cover_photo_id ?? null,
      created_at: gallery.created_at,
      updated_at: gallery.updated_at ?? gallery.created_at,
      // GAL-10 — demo galleries render whichever design the studio
      // picked in Preview Studio; never gated.
      template: (gallery.template as GalleryTemplateId | undefined) ?? DEFAULT_TEMPLATE_ID,
      customization: (gallery.customization as GalleryCustomization | undefined) ?? {},
      pin_required: false,
    },
    locked: false,
    photos: photos.map((p) => ({
      id: p.id,
      file_url: p.file_url ?? p.url,
      thumbnail_url: p.thumbnail_url ?? null,
      name: p.name,
      sort_order: p.sort_order ?? 0,
      width: p.width ?? null,
      height: p.height ?? null,
      ai_tags: p.ai_tags ?? null,
      ai_scene: p.ai_scene ?? null,
      ai_dominant_colors: p.ai_dominant_colors ?? null,
    })),
    // The demo studio's identity. Matches the rest of the cast; the
    // brand colour is Orbit's default gold, same fallback the RPC path
    // uses when a studio hasn't set one.
    branding: {
      studio_name: 'Orbit Studio',
      logo_url: null,
      brand_color: '#C9A16B',
    },
    // No watermark on the demo cast — these are the photographer's own
    // portfolio frames and they should look like finished work.
    watermark: null,
    // Gallery videos ride the Mux delivery pipeline, which Preview
    // Studio never touches. See the note on `useGalleryVideos`.
    videos: [],
  };
}

// GAL-HANG-1 — same lesson as useAuth's HOTFIX-1: a promise with
// no terminal state (the RPC never resolves or rejects — a stalled
// connection, a paused project, a stuck PostgREST worker) left this
// query's `isLoading` true forever, which is exactly the "public
// gallery just spins" report. Racing the RPC against a hard
// timeout guarantees a terminal state every time — success, a real
// error, or a clear timeout error — so the page can always reach
// "not found" / retry instead of spinning indefinitely.
const GALLERY_RPC_TIMEOUT_MS = 12_000;

function withTimeout<T>(promise: PromiseLike<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new Error(
          `This gallery didn't load within ${Math.round(ms / 1000)}s. Check your connection and try again.`,
        ),
      );
    }, ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export const usePublicGalleryById = (galleryId: string | undefined) => {
  // `canUseDemo` (not `isDemo`) on purpose: the studio may open the
  // client link in a second tab, where the session-local `isDemo`
  // toggle is false but the admin grant still holds.
  const { isDemo, canUseDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: [
      'public-gallery',
      galleryId,
      // `canUseDemo` rides in the key so an admin granting (or
      // revoking) Preview Studio access re-resolves a demo link
      // immediately instead of serving a cached "not found".
      canUseDemo,
      isDemo ? demoStore.version : 0,
    ],
    queryFn: async (): Promise<PublicGalleryPayload | null> => {
      if (!galleryId) return null;

      if (isDemoId(galleryId)) {
        if (!canUseDemo && !isDemo) {
          // Not a demo-granted viewer — behave exactly as if the id
          // didn't exist, which is what the RPC would say anyway.
          return null;
        }
        const gallery =
          demoStore.galleries.list().find((g) => g.id === galleryId) ??
          (await import('@/data/demoOverlay')).demoGalleries.find(
            (g) => g.id === galleryId,
          );
        if (!gallery) return null;
        // Draft galleries 404 for clients — the whole point of the
        // Publish beat. Preview Studio must not be more permissive
        // than production here, or "publish" means nothing on stage.
        if (!gallery.is_public) return null;
        const stored = demoStore.galleryPhotos.list(galleryId);
        const photos =
          stored.length > 0
            ? stored
            : ((await import('@/data/demoOverlay')).demoGalleryPhotos[galleryId] ?? []);
        return buildDemoPublicPayload(gallery, photos);
      }

      const { data, error } = await withTimeout<any>(
        (supabase as any).rpc('get_public_gallery_by_id', {
          p_gallery_id: galleryId,
        }),
        GALLERY_RPC_TIMEOUT_MS,
      );
      if (error) throw error;
      // The RPC returns NULL when the gallery is hidden. Surface
      // that as an actual `null` so the page can render "not found"
      // rather than throw.
      return (data ?? null) as PublicGalleryPayload | null;
    },
    enabled: !!galleryId,
    // Photos rarely change mid-view; the studio adding one bumps the
    // cache invalidation via the CRUD hooks above.
    staleTime: 2 * 60_000,
  });
};
